/**
 * Builds OpenSearch nested aggregation queries for GROUP BY mode.
 *
 * Structure: nested terms aggs (one per group-by field) with metric sub-aggs
 * on the innermost level for each non-group-by column.
 *
 * Example output for GROUP BY [company, status] with SUM(amount), COUNT(name):
 * {
 *   "group_by_company": {
 *     "terms": { "field": "company", "size": 500 },
 *     "aggs": {
 *       "group_by_status": {
 *         "terms": { "field": "status", "size": 500 },
 *         "aggs": {
 *           "agg_amount": { "sum": { "field": "amount" } },
 *           "agg_name": { "value_count": { "field": "name" } }
 *         }
 *       }
 *     }
 *   }
 * }
 */

import type { AggregationType, ColumnAggregationMap, DateGroupingMap } from './aggregation-types';
import type { FieldOption } from './opensearch-field-mapper';
import type { SortEntry } from './sort-helpers';

/** Max buckets per terms aggregation level */
const TERMS_SIZE = 500;

/**
 * Composite-aggregation identifiers. The report **preview** uses a composite
 * aggregation so grouping never exceeds OpenSearch's `search.max_buckets`: it
 * materializes only one page of buckets at a time and paginates via `after_key`,
 * so the bucket count never depends on the product of group-by cardinalities.
 * Dashboard widgets keep using the nested `terms` builder above; the CSV export
 * has its own composite implementation server-side (tio-api).
 */
export const COMPOSITE_AGG_NAME = 'grouped';
/** Buckets requested per composite page */
export const COMPOSITE_PAGE_SIZE = 1000;

type SortDirection = 'asc' | 'desc';

interface AggregationSort {
  field: string;
  direction: SortDirection;
}

/**
 * Convert camelCase field key to snake_case for OpenSearch.
 * The /fields endpoint returns camelCase names but OpenSearch indexes use snake_case.
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Map our AggregationType to OpenSearch aggregation DSL type */
function aggTypeToOsDsl(aggType: AggregationType): string {
  switch (aggType) {
    case 'count':
      return 'value_count';
    case 'sum':
      return 'sum';
    case 'avg':
      return 'avg';
    case 'min':
      return 'min';
    case 'max':
      return 'max';
    case 'cardinality':
      return 'cardinality';
    // `avg` over a boolean is the fraction of true docs (0..1); the percent
    // display format scales it for both the preview and the server's CSV export.
    case 'percent_true':
      return 'avg';
    default:
      return 'value_count';
  }
}

/**
 * Resolve the OpenSearch field path safe to use inside a metric aggregation.
 *
 * Aggregations require doc_values (or fielddata), so analyzed `text` fields
 * cannot be aggregated directly — OpenSearch returns
 * "Text fields are not optimised for operations that require per-document field data"
 * and the entire shard request fails.
 *
 * Reuses the canonical doc-values-safe path that `field-filter-pipeline`
 * already derives onto `FieldOption.sortPath` (same path used for sorting in
 * `preview-table.gts`). For text fields this is typically the `.keyword`
 * subfield; for numeric/date/keyword fields it's the raw path. Falls back to
 * `path` then `camelToSnake(key)` for legacy callers without `sortPath`.
 */
function aggFieldFor(col: FieldOption): string {
  return col.sortPath ?? col.path ?? camelToSnake(col.key);
}

function parseSortField(sortField: string): AggregationSort {
  const field = sortField.replace(/:(?:asc|desc)$/, '');
  const direction = sortField.endsWith(':desc') ? 'desc' : 'asc';
  return { field, direction };
}

export function parseSorts(sorts: SortEntry[] | string | undefined): AggregationSort[] {
  if (!sorts) return [];
  if (typeof sorts === 'string') return [parseSortField(sorts)];
  return sorts.map((s) => ({ field: s.key, direction: s.direction }));
}

function sortMatchesColumn(sortField: string, col: FieldOption) {
  return [col.sortPath, col.path, col.key, col.resultKey].includes(sortField);
}

function sortColumnFor(sort: AggregationSort, columns: FieldOption[]) {
  return columns.find((col) => sortMatchesColumn(sort.field, col));
}

function orderForGroupByField(
  fieldKey: string,
  sortEntries: AggregationSort[],
  columns: FieldOption[]
): Record<string, SortDirection>[] {
  const orders: Record<string, SortDirection>[] = [];
  for (const sort of sortEntries) {
    const sortCol = sortColumnFor(sort, columns);
    if (
      sortCol?.key === fieldKey ||
      sort.field === fieldKey ||
      sort.field === camelToSnake(fieldKey)
    ) {
      orders.push({ _key: sort.direction });
    }
  }
  return orders;
}

function orderForMetricFields(
  isInnermost: boolean,
  groupByKeys: string[],
  sortEntries: AggregationSort[],
  columns: FieldOption[]
): Record<string, SortDirection>[] {
  if (!isInnermost) return [];
  const orders: Record<string, SortDirection>[] = [];
  for (const sort of sortEntries) {
    const sortCol = sortColumnFor(sort, columns);
    if (sortCol && !groupByKeys.includes(sortCol.key)) {
      orders.push({ [`agg_${sortCol.key}`]: sort.direction });
    }
  }
  return orders;
}

/**
 * Build metric sub-aggregations for the innermost level.
 * One sub-agg per non-group-by column.
 */
function buildMetricAggs(
  columns: FieldOption[],
  groupByKeys: string[],
  columnAggregations: ColumnAggregationMap
): Record<string, unknown> {
  const aggs: Record<string, unknown> = {};

  for (const col of columns) {
    // Skip group-by dimension fields — they appear as bucket keys
    if (groupByKeys.includes(col.key)) continue;

    const aggType = columnAggregations[col.key] || 'count';
    const osDslType = aggTypeToOsDsl(aggType);

    aggs[`agg_${col.key}`] = {
      [osDslType]: { field: aggFieldFor(col) },
    };
  }

  return aggs;
}

/**
 * Build the nested aggregation structure.
 *
 * Uses `date_histogram` for date fields that have a grouping interval,
 * and `terms` for all other fields.
 *
 * @param groupByKeys - ordered array of field keys to group by
 * @param columns - all selected columns (including group-by fields)
 * @param columnAggregations - map of columnKey → AggregationType
 * @param sorts - ordered sort entries (SortEntry[], legacy string, or undefined)
 * @param dateGroupingIntervals - map of fieldKey → DateGroupingInterval for date fields
 * @returns OpenSearch aggregations object to merge into the query body
 */
export function buildAggregationQuery(
  groupByKeys: string[],
  columns: FieldOption[],
  columnAggregations: ColumnAggregationMap,
  sorts?: SortEntry[] | string,
  dateGroupingIntervals?: DateGroupingMap,
  havingSelectors?: Record<string, unknown>
): Record<string, unknown> {
  if (groupByKeys.length === 0) return {};

  const metricAggs = buildMetricAggs(columns, groupByKeys, columnAggregations);
  // HAVING (bucket_selector) clauses sit alongside the metric aggs they
  // reference, at the innermost bucket level.
  if (havingSelectors) {
    Object.assign(metricAggs, havingSelectors);
  }
  const sortEntries = parseSorts(sorts);

  // Build from innermost → outermost
  let currentAggs: Record<string, unknown> = metricAggs;

  for (let i = groupByKeys.length - 1; i >= 0; i--) {
    const fieldKey = groupByKeys[i]!;
    const aggName = `group_by_${fieldKey}`;
    const explicitInterval = dateGroupingIntervals?.[fieldKey];
    const fieldColumn = columns.find((c) => c.key === fieldKey);
    const isDateField =
      fieldColumn?.isDate || fieldColumn?.type === 'date' || fieldColumn?.reportDatatype === 'date';
    const dateInterval = explicitInterval || (isDateField ? 'year' : undefined);
    const fieldPath = fieldColumn ? aggFieldFor(fieldColumn) : camelToSnake(fieldKey);
    const isInnermost = i === groupByKeys.length - 1;

    const groupOrders = orderForGroupByField(fieldKey, sortEntries, columns);
    const metricOrders = orderForMetricFields(isInnermost, groupByKeys, sortEntries, columns);
    const allOrders = [...groupOrders, ...metricOrders];
    const order =
      allOrders.length === 1 ? allOrders[0] : allOrders.length > 1 ? allOrders : undefined;

    let fieldAgg: Record<string, unknown>;

    if (dateInterval) {
      fieldAgg = {
        date_histogram: {
          field: fieldPath,
          calendar_interval: dateInterval,
          format: dateHistogramFormat(dateInterval),
          min_doc_count: 1,
          ...(order ? { order } : {}),
        },
      };
    } else {
      fieldAgg = {
        terms: { field: fieldPath, size: TERMS_SIZE, ...(order ? { order } : {}) },
      };
    }

    if (Object.keys(currentAggs).length > 0) {
      fieldAgg['aggs'] = currentAggs;
    }

    currentAggs = { [aggName]: fieldAgg };
  }

  return currentAggs;
}

/**
 * Direction to order a composite source (`terms`/`date_histogram`) by, when the
 * user sorts by that group-by column. Composite can only order by its source
 * keys — not by a metric — so metric-column sorts are handled client-side.
 */
function groupSortDirection(
  fieldKey: string,
  sortEntries: AggregationSort[],
  columns: FieldOption[]
): SortDirection | undefined {
  for (const sort of sortEntries) {
    const sortCol = sortColumnFor(sort, columns);
    if (
      sortCol?.key === fieldKey ||
      sort.field === fieldKey ||
      sort.field === camelToSnake(fieldKey)
    ) {
      return sort.direction;
    }
  }
  return undefined;
}

/**
 * Build a COMPOSITE aggregation for GROUP BY mode.
 *
 * Unlike {@link buildAggregationQuery} (nested `terms`, whose total bucket count
 * is the product of per-level cardinalities and can blow past
 * `search.max_buckets`), a composite aggregation emits one source per group-by
 * column and returns a single flat list of buckets, one page at a time — so it
 * never approaches the limit no matter how many group-by columns are combined.
 * Callers paginate with the response `after_key` (see `flattenCompositeBuckets`).
 *
 * Trade-off: composite orders only by its source keys (asc/desc per source), so
 * sorting a report by a *metric* column must be done client-side on the fetched
 * rows. Group-by column sorts map to per-source `order`.
 *
 * @param after - composite `after` key for the next page (omit for the first page)
 * @param pageSize - buckets per page (defaults to {@link COMPOSITE_PAGE_SIZE})
 */
export function buildCompositeAggregationQuery(
  groupByKeys: string[],
  columns: FieldOption[],
  columnAggregations: ColumnAggregationMap,
  sorts?: SortEntry[] | string,
  dateGroupingIntervals?: DateGroupingMap,
  havingSelectors?: Record<string, unknown>,
  after?: Record<string, unknown>,
  pageSize: number = COMPOSITE_PAGE_SIZE
): Record<string, unknown> {
  if (groupByKeys.length === 0) return {};

  const metricAggs = buildMetricAggs(columns, groupByKeys, columnAggregations);
  // HAVING (bucket_selector) clauses sit alongside the metric aggs under the
  // composite; they filter the buckets collected for each page.
  if (havingSelectors) {
    Object.assign(metricAggs, havingSelectors);
  }
  const sortEntries = parseSorts(sorts);

  const sources = groupByKeys.map((fieldKey) => {
    const fieldColumn = columns.find((c) => c.key === fieldKey);
    const explicitInterval = dateGroupingIntervals?.[fieldKey];
    const isDateField =
      fieldColumn?.isDate || fieldColumn?.type === 'date' || fieldColumn?.reportDatatype === 'date';
    const dateInterval = explicitInterval || (isDateField ? 'year' : undefined);
    const fieldPath = fieldColumn ? aggFieldFor(fieldColumn) : camelToSnake(fieldKey);
    const direction = groupSortDirection(fieldKey, sortEntries, columns);

    const valueSource: Record<string, unknown> = dateInterval
      ? {
          date_histogram: {
            field: fieldPath,
            calendar_interval: dateInterval,
            format: dateHistogramFormat(dateInterval),
            ...(direction ? { order: direction } : {}),
          },
        }
      : {
          terms: { field: fieldPath, ...(direction ? { order: direction } : {}) },
        };

    return { [`group_by_${fieldKey}`]: valueSource };
  });

  const composite: Record<string, unknown> = { size: pageSize, sources };
  if (after) {
    composite['after'] = after;
  }

  const groupedAgg: Record<string, unknown> = { composite };
  if (Object.keys(metricAggs).length > 0) {
    groupedAgg['aggs'] = metricAggs;
  }

  return { [COMPOSITE_AGG_NAME]: groupedAgg };
}

/**
 * Get the OpenSearch date format string for a date_histogram interval.
 * Controls what `key_as_string` returns in each bucket.
 */
function dateHistogramFormat(interval: string): string {
  switch (interval) {
    case 'year':
      return 'yyyy';
    case 'quarter':
      return 'yyyy-QQQ';
    case 'month':
      return 'yyyy-MM';
    case 'week':
      return 'yyyy-MM-dd';
    case 'day':
      return 'yyyy-MM-dd';
    default:
      return 'yyyy-MM-dd';
  }
}
